/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { AnalyticsLogger } from "./AnalyticsLogger";
import { DiagnosticLogger } from "./DiagnosticLogger";
import { OperationalLogger } from "./OperationalLogger";
import type { LogDetails } from "../types";
import { DiagnosticSeverity } from "../types";

/**
 * Sign-In phase telemetry facade.
 *
 * Scope is the SIGN-IN phase only: from auth-type resolution
 * (`resolveNextAuthStep`) through domain-token acquisition and device
 * provisioning. PreSignIn (`pre_signin_api` / `pre_signin_screen`, owned by
 * UAW-2418/2421) and PostSignIn (UAW-2420) emit under their own event names —
 * do not re-tag them here.
 *
 * Sign-in runs entirely before Sage identity (bodyId / partnerId) exists, so
 * every call below rides the pending-telemetry queue and is replayed once
 * provisioning completes. Nothing here blocks or throws.
 *
 * Diagnostics are backhaul-gated and are for triage; operational events are
 * always on and are what fleet dashboards and alerts read. An event that has
 * to be visible without backhaul belongs in the operational section.
 *
 * Zero-PII: never pass a username, password, email, reset token, JWT, license
 * plate code, PCID or raw IP into any method on this class. Pass the error
 * *type* and status code instead — see `logging-guidelines.md`.
 */

/**
 * Caller-supplied fields. Unlike {@link LogDetails} a value may be undefined:
 * the facade drops those keys, so a call site can write a conditional field
 * inline instead of building the object in two steps.
 */
export type SignInLogFields = Record<string, string | undefined>;

/** Diagnostic event names owned by the sign-in phase. */
const EVENT_API = "signin_api";
const EVENT_FLOW = "signin_flow";
const EVENT_SCREEN = "signin_screen";

/**
 * Operational event names. `apiRequest` is shared with `CarouselLogger` and
 * must stay byte-identical to it; the rest are sign-in specific and are kept
 * camelCase to match the Android client for shared dashboards.
 */
const EVENT_API_REQUEST = "apiRequest";
const EVENT_SIGN_IN_START = "signInStart";
const EVENT_SIGN_IN_END = "signInEnd";
const EVENT_SIGN_IN_FALLBACK = "signInFallback";
const EVENT_SIGN_IN_BLOCKED = "signInBlocked";
const EVENT_LICENSE_PLATE_ISSUED = "licensePlateIssued";
const EVENT_LICENSE_PLATE_EXPIRED = "licensePlateExpired";
const EVENT_SESSION_RESTORED = "sessionRestored";

const KEY_API_NAME = "api_name";

/* eslint-disable sonarjs/no-hardcoded-passwords -- These are telemetry event
   names, not credentials. Keys such as ForgotPassword / ResetPassword name the
   endpoint or screen the event describes; the values are the literals that go
   on the wire. No secret is stored in this file, and by design none can be:
   every method here takes only pre-redacted fields. */

/**
 * Analytics event names. `signIn` and `viewedScreen` are spec-owned (P1) and
 * shared with Android; the rest are sign-in specific and follow the repo's
 * snake_case convention for locally-invented events.
 */
const EVENT_SIGN_IN = "signIn";
const EVENT_VIEWED_SCREEN = "viewedScreen";
const EVENT_SIGN_IN_INTERACTION = "signin_interaction";
const EVENT_PASSWORD_RESET_REQUESTED = "password_reset_requested";
const EVENT_PASSWORD_RESET_COMPLETED = "password_reset_completed";

const EVENT_TYPE_IMPRESSION = "impression";
const EVENT_TYPE_CLICK = "click";

const KEY_SCREEN_ID = "screen_id";
const KEY_SCREEN_NAME = "screen_name";
const KEY_PREVIOUS_SCREEN_ID = "previous_screen_id";
const KEY_CONTROL_ID = "control_id";

/**
 * Unity authentication type → the spec's `login_type` enum. Types with no
 * honest spec value are absent; see {@link SignInLogger.toSpecLoginType}.
 */
const LOGIN_TYPE_BY_AUTH_TYPE: Record<string, string | undefined> = {
  tivoNativeIdp: "manual",
  msoNativeIdp: "2Pass",
  saml2WebProfileIdp: "SAML"
};

/** `result` / `status` values on operational sign-in events. */
export const SignInResult = {
  Success: "success",
  Failure: "failure"
} as const;

export type SignInResultName = (typeof SignInResult)[keyof typeof SignInResult];

/** Controls on the sign-in screens. Values are the `control_id` field. */
export const SignInControl = {
  TabUserIdPassword: "tab_user_id_password",
  TabUseCode: "tab_use_code",
  SubmitSignIn: "submit_sign_in",
  NeedHelp: "need_help",
  GetNewCode: "get_new_code",
  ForgotPassword: "forgot_password"
} as const;

export type SignInControlName =
  (typeof SignInControl)[keyof typeof SignInControl];

const STATUS_SUCCESS = "success";
const STATUS_FAILURE = "failure";
const UNKNOWN_ERROR_TYPE = "unknown";

const KEY_API = "api";
const KEY_STATUS = "status";
const KEY_EVENT = "event";
const KEY_SCREEN = "screen";
const KEY_ERROR_TYPE = "error_type";
const KEY_HTTP_STATUS = "http_status";
const KEY_LATENCY_MS = "latency_ms";
const KEY_ATTEMPT = "attempt";

const SCREEN_EVENT_MOUNTED = "mounted";
const SCREEN_EVENT_UNMOUNTED = "unmounted";

/**
 * Log every Nth DBS poll attempt. A license plate lives for minutes and polls
 * every few seconds, so logging each tick would evict the earlier startup and
 * sign-in entries from the bounded pending-log queue.
 */
const DBS_POLL_LOG_INTERVAL = 5;

/** Service calls made during the sign-in phase. Values are the `api` field. */
export const SignInApi = {
  Claim: "claim",
  IdpJwtToken: "idpJwtToken",
  IpAutologinToken: "ipAutologinToken",
  LicensePlateV2: "licensePlateV2",
  DbsJwtToken: "dbsJwtToken",
  Bind: "bind",
  DomainToken: "domainToken",
  ProvisionDevicesV2: "provisionDevicesV2",
  ForgotPassword: "forgotPassword",
  ValidateEmailToken: "validateEmailToken",
  UpdatePassword: "updatePassword"
} as const;

export type SignInApiName = (typeof SignInApi)[keyof typeof SignInApi];

/**
 * Steps within a sign-in flow. Values are the `step` / `step_reached` field.
 * Most mirror {@link SignInApi} so a step and the call it makes share a value.
 */
export const SignInStep = {
  Unknown: UNKNOWN_ERROR_TYPE,
  Config: "config",
  Claim: SignInApi.Claim,
  IdpJwtToken: SignInApi.IdpJwtToken,
  IpAutologinToken: SignInApi.IpAutologinToken,
  LicensePlate: SignInApi.LicensePlateV2,
  DbsJwtToken: SignInApi.DbsJwtToken,
  Bind: SignInApi.Bind,
  DomainToken: SignInApi.DomainToken,
  ProvisionDevices: SignInApi.ProvisionDevicesV2
} as const;

export type SignInStepName = (typeof SignInStep)[keyof typeof SignInStep];

/** Sign-in sub-flows. Values are the `flow` field. */
export const SignInFlow = {
  Orchestration: "orchestration",
  Credentials: "credentials",
  Dbs: "dbs",
  IpAutoLogin: "ipAutoLogin",
  SessionRestore: "sessionRestore",
  PasswordReset: "passwordReset",
  /** Post-sign-in DPS re-run after a `/secure` 403. */
  Reprovision: "reprovision"
} as const;

export type SignInFlowName = (typeof SignInFlow)[keyof typeof SignInFlow];

/** `signin_flow` event discriminators. Values are the `event` field. */
export const SignInFlowEvent = {
  AuthTypeResolved: "auth_type_resolved",
  AuthTypeSkipped: "auth_type_skipped",
  AuthTypeFallback: "auth_type_fallback",
  AuthSchemeFallback: "auth_scheme_fallback",
  FlowStarted: "flow_started",
  FlowCompleted: "flow_completed",
  FlowFailed: "flow_failed",
  IpAutologinTimeout: "ip_autologin_timeout",
  DbsPollAttempt: "dbs_poll_attempt",
  DbsPollExpired: "dbs_poll_expired",
  DbsPollCancelled: "dbs_poll_cancelled",
  RetryOverlayShown: "retry_overlay_shown",
  RetryOverlayDismissed: "retry_overlay_dismissed",
  TokenRehydrated: "token_rehydrated",
  TokenRehydrateFailed: "token_rehydrate_failed",
  SignInErrorShown: "signin_error_shown",
  /** DPS 410 — the DLID was removed server-side and a new identity generated. */
  DeviceIdentityReset: "device_identity_reset"
} as const;

export type SignInFlowEventName =
  (typeof SignInFlowEvent)[keyof typeof SignInFlowEvent];

/** Screens that belong to the sign-in phase. Values are the `screen` field. */
export const SignInScreen = {
  SignIn: "SignIn",
  UserIdPassword: "UserIdPasswordScreen",
  UseCode: "UseCodeScreen",
  RecoverPassword: "RecoverPassword",
  ResetPassword: "ResetPassword"
} as const;

export type SignInScreenName = (typeof SignInScreen)[keyof typeof SignInScreen];

/* eslint-enable sonarjs/no-hardcoded-passwords */

export class SignInLogger {
  /** Last sign-in screen reported, for viewedScreen's previous_screen_id. */
  private static mPreviousScreenId: string | undefined = undefined;

  // ─────────────────────────── Diagnostic Events ───────────────────────────

  /**
   * One successful physical service call — emits both the `signin_api`
   * diagnostic (triage depth, backhaul-gated) and the `apiRequest`
   * operational event (always-on service health).
   *
   * Emitted from the interactor rather than the thunks so that all three
   * sign-in flows are covered from one place, and so that each
   * `RetryMechanism` attempt produces its own pair.
   */
  public static logApiSuccess(
    api: SignInApiName,
    latencyMs: number,
    extra?: SignInLogFields
  ): void {
    const details: LogDetails = {};
    details[KEY_API] = api;
    details[KEY_STATUS] = STATUS_SUCCESS;
    details[KEY_LATENCY_MS] = String(latencyMs);
    SignInLogger.assign(details, extra);

    DiagnosticLogger.log(
      EVENT_API,
      api + " succeeded",
      DiagnosticSeverity.Info,
      details
    );
    SignInLogger.logApiRequest(api, STATUS_SUCCESS, latencyMs, extra);
  }

  /**
   * One failed physical service call — emits both the `signin_api` diagnostic
   * and the `apiRequest` operational event.
   *
   * The diagnostic omits `latency_ms`, matching the PreSignIn convention;
   * `apiRequest` keeps it, because on a service-health dashboard an instant
   * 401 and a 60-second timeout are not the same failure.
   *
   * Only the error *type* and HTTP status are recorded, never the service
   * message: it can echo back the username the caller supplied.
   */
  public static logApiFailure(
    api: SignInApiName,
    error: unknown,
    severity: DiagnosticSeverity,
    latencyMs: number,
    extra?: SignInLogFields
  ): void {
    const errorType: string = SignInLogger.describeErrorType(error);
    const httpStatus: string | undefined =
      SignInLogger.extractHttpStatus(error);

    const details: LogDetails = {};
    details[KEY_API] = api;
    details[KEY_STATUS] = STATUS_FAILURE;
    details[KEY_ERROR_TYPE] = errorType;
    if (httpStatus !== undefined) {
      details[KEY_HTTP_STATUS] = httpStatus;
    }
    SignInLogger.assign(details, extra);

    DiagnosticLogger.log(EVENT_API, api + " failed", severity, details);
    SignInLogger.logApiRequest(api, STATUS_FAILURE, latencyMs, {
      ...extra,
      error_type: errorType,
      http_status: httpStatus
    });
  }

  /**
   * signin_flow — a state-machine transition or orchestration decision.
   */
  public static logFlowEvent(
    event: SignInFlowEventName,
    message: string,
    severity: DiagnosticSeverity,
    extra?: SignInLogFields
  ): void {
    const details: LogDetails = {};
    details[KEY_EVENT] = event;
    SignInLogger.assign(details, extra);

    DiagnosticLogger.log(EVENT_FLOW, message, severity, details);
  }

  /**
   * signin_flow — a flow failure carrying the raw error.
   *
   * Convenience wrapper that applies the same error-type / http-status
   * redaction as {@link logApiFailure}.
   */
  public static logFlowFailure(
    event: SignInFlowEventName,
    message: string,
    error: unknown,
    extra?: SignInLogFields
  ): void {
    const details: LogDetails = {};
    details[KEY_ERROR_TYPE] = SignInLogger.describeErrorType(error);

    const httpStatus: string | undefined =
      SignInLogger.extractHttpStatus(error);
    if (httpStatus !== undefined) {
      details[KEY_HTTP_STATUS] = httpStatus;
    }
    SignInLogger.assign(details, extra);

    SignInLogger.logFlowEvent(
      event,
      message,
      DiagnosticSeverity.Error,
      details
    );
  }

  /**
   * signin_flow — a single DBS license-plate poll tick, sampled.
   *
   * Logs the first attempt, every {@link DBS_POLL_LOG_INTERVAL}th attempt, and
   * always the terminal one. Callers do not need to apply the rule themselves.
   */
  public static logDbsPollAttempt(
    attempt: number,
    isFinalAttempt: boolean,
    extra?: SignInLogFields
  ): void {
    if (!isFinalAttempt && !SignInLogger.shouldLogPollAttempt(attempt)) {
      return;
    }
    const details: LogDetails = {};
    details[KEY_ATTEMPT] = String(attempt);
    SignInLogger.assign(details, extra);

    SignInLogger.logFlowEvent(
      SignInFlowEvent.DbsPollAttempt,
      "DBS binding poll attempt " + String(attempt),
      DiagnosticSeverity.Debug,
      details
    );
  }

  /** True when this poll attempt falls on the sampling boundary. */
  public static shouldLogPollAttempt(attempt: number): boolean {
    return attempt === 1 || attempt % DBS_POLL_LOG_INTERVAL === 0;
  }

  /**
   * signin_screen — a sign-in screen became visible.
   */
  public static logScreenMounted(
    screen: SignInScreenName,
    extra?: SignInLogFields
  ): void {
    const details: LogDetails = {};
    details[KEY_SCREEN] = screen;
    details[KEY_EVENT] = SCREEN_EVENT_MOUNTED;
    SignInLogger.assign(details, extra);

    DiagnosticLogger.log(
      EVENT_SCREEN,
      screen + " mounted",
      DiagnosticSeverity.Info,
      details
    );
  }

  /**
   * signin_screen — a sign-in screen was torn down, with the time it was up.
   */
  public static logScreenUnmounted(
    screen: SignInScreenName,
    visibleDurationMs: number,
    extra?: SignInLogFields
  ): void {
    const details: LogDetails = {};
    details[KEY_SCREEN] = screen;
    details[KEY_EVENT] = SCREEN_EVENT_UNMOUNTED;
    details.visible_duration_ms = String(visibleDurationMs);
    SignInLogger.assign(details, extra);

    DiagnosticLogger.log(
      EVENT_SCREEN,
      screen + " unmounted",
      DiagnosticSeverity.Info,
      details
    );
  }

  // ────────────────────────── Operational Events ───────────────────────────
  //
  // Not backhaul-gated, unlike the diagnostics above — anything an NOC
  // dashboard or alert needs lives here.

  /**
   * signInStart — opens the transaction that brackets one sign-in attempt.
   *
   * Returns the transaction id; pass it to {@link logSignInEnd} or
   * {@link logSignInCancelled} so the pair correlates on a dashboard.
   */
  public static logSignInStart(fields: SignInLogFields): string {
    return OperationalLogger.transactionStart(
      EVENT_SIGN_IN_START,
      SignInLogger.toDetails(fields)
    );
  }

  /**
   * signInEnd — closes the attempt with its outcome and per-step timings.
   */
  public static logSignInEnd(
    transactionId: string,
    fields: SignInLogFields
  ): void {
    OperationalLogger.transactionEnd(
      EVENT_SIGN_IN_END,
      transactionId,
      SignInLogger.toDetails(fields)
    );
  }

  /**
   * Cancels the attempt's transaction — the user navigated away, the app was
   * backgrounded, or the abort signal fired mid-flow.
   */
  public static logSignInCancelled(
    transactionId: string,
    reason: string,
    fields?: SignInLogFields
  ): void {
    OperationalLogger.transactionCancel(
      transactionId,
      reason,
      SignInLogger.toDetails(fields)
    );
  }

  /**
   * apiRequest — service health for one sign-in call.
   *
   * Deliberately the same event and field names `CarouselLogger` already
   * emits, so sign-in rows land in the existing service-health dashboard
   * rather than a parallel one.
   */
  public static logApiRequest(
    apiName: SignInApiName,
    status: string,
    latencyMs: number,
    extra?: SignInLogFields
  ): void {
    const details: LogDetails = {};
    details[KEY_API_NAME] = apiName;
    details[KEY_STATUS] = status;
    details[KEY_LATENCY_MS] = String(latencyMs);
    SignInLogger.assign(details, extra);

    OperationalLogger.logEvent(EVENT_API_REQUEST, details);
  }

  /**
   * signInFallback — the orchestrator moved on to another auth type. A spike
   * means an MSO is advertising auth configuration this client cannot honour.
   */
  public static logSignInFallback(fields: SignInLogFields): void {
    OperationalLogger.logEvent(
      EVENT_SIGN_IN_FALLBACK,
      SignInLogger.toDetails(fields)
    );
  }

  /**
   * signInBlocked — the actionless connection-error overlay went up while a
   * CRITICAL retry backs off. The device is stuck with no user escape, so
   * this is the alertable one.
   */
  public static logSignInBlocked(fields: SignInLogFields): void {
    OperationalLogger.logEvent(
      EVENT_SIGN_IN_BLOCKED,
      SignInLogger.toDetails(fields)
    );
  }

  /** licensePlateIssued — top of the DBS binding funnel. */
  public static logLicensePlateIssued(fields: SignInLogFields): void {
    OperationalLogger.logEvent(
      EVENT_LICENSE_PLATE_ISSUED,
      SignInLogger.toDetails(fields)
    );
  }

  /** licensePlateExpired — the user never completed the bind in time. */
  public static logLicensePlateExpired(fields: SignInLogFields): void {
    OperationalLogger.logEvent(
      EVENT_LICENSE_PLATE_EXPIRED,
      SignInLogger.toDetails(fields)
    );
  }

  /** sessionRestored — a returning device signed in from persisted state. */
  public static logSessionRestored(fields: SignInLogFields): void {
    OperationalLogger.logEvent(
      EVENT_SESSION_RESTORED,
      SignInLogger.toDetails(fields)
    );
  }

  // ─────────────────────────── Analytics Events ────────────────────────────
  //
  // `signIn` and `viewedScreen` are spec-owned and shared with Android, so
  // their names and field names come from the Unity Client Logging
  // Specification, not from this repo. Sign-in-specific events that the spec
  // does not define use the repo's snake_case convention instead of bending a
  // spec event to fit.

  /**
   * signIn — the spec's P1 analytics event, one per user-visible attempt.
   *
   * `login_type` is the spec's enum (`auth` category); `auth_type` carries the
   * exact Unity authentication type alongside it so nothing is lost where the
   * spec enum has no matching value.
   */
  public static logSignInAnalytics(fields: SignInLogFields): void {
    AnalyticsLogger.trackEvent(
      EVENT_SIGN_IN,
      undefined,
      SignInLogger.toDetails(fields)
    );
  }

  /**
   * Maps a Unity authentication type onto the spec's `login_type` enum
   * (`2Pass` | `SSO` | `SAML` | `manual`).
   *
   * Returns undefined for `tivoIpAutoLoginIdp` and `dbsLicensePlateBinding`:
   * the spec enum predates both and has no value that honestly describes
   * them. The field is omitted rather than given an invented value that would
   * corrupt a dashboard shared with the other clients — `auth_type` still
   * carries the precise value. See the note in signin-phase-logging-plan.md.
   */
  public static toSpecLoginType(authType: string): string | undefined {
    return LOGIN_TYPE_BY_AUTH_TYPE[authType];
  }

  /**
   * viewedScreen — the spec's P1 impression event.
   *
   * `previous_screen_id` is tracked here rather than by each caller: the
   * facade already sees every sign-in screen in the order they are shown, and
   * threading it through the views would duplicate that for no gain.
   */
  public static logViewedScreen(
    screenId: SignInScreenName,
    screenName: string,
    extra?: SignInLogFields
  ): void {
    const details: LogDetails = {};
    details[KEY_SCREEN_ID] = screenId;
    details[KEY_SCREEN_NAME] = screenName;
    if (SignInLogger.mPreviousScreenId !== undefined) {
      details[KEY_PREVIOUS_SCREEN_ID] = SignInLogger.mPreviousScreenId;
    }
    SignInLogger.assign(details, extra);
    SignInLogger.mPreviousScreenId = screenId;

    AnalyticsLogger.trackEvent(
      EVENT_VIEWED_SCREEN,
      EVENT_TYPE_IMPRESSION,
      details
    );
  }

  /**
   * signin_interaction — a control on a sign-in screen was activated.
   *
   * Deliberately not the spec's `itemSelected`: that event's fields
   * (`tile_id`, `tile_position`, `list_name`) describe content tiles in a
   * carousel, and a tab or a submit button is neither.
   */
  public static logInteraction(
    control: SignInControlName,
    screenId: SignInScreenName,
    extra?: SignInLogFields
  ): void {
    const details: LogDetails = {};
    details[KEY_CONTROL_ID] = control;
    details[KEY_SCREEN_ID] = screenId;
    SignInLogger.assign(details, extra);

    AnalyticsLogger.trackEvent(
      EVENT_SIGN_IN_INTERACTION,
      EVENT_TYPE_CLICK,
      details
    );
  }

  /** password_reset_requested — the user asked for a reset link. */
  public static logPasswordResetRequested(fields?: SignInLogFields): void {
    AnalyticsLogger.trackEvent(
      EVENT_PASSWORD_RESET_REQUESTED,
      undefined,
      SignInLogger.toDetails(fields)
    );
  }

  /** password_reset_completed — the new password was accepted, or was not. */
  public static logPasswordResetCompleted(fields: SignInLogFields): void {
    AnalyticsLogger.trackEvent(
      EVENT_PASSWORD_RESET_COMPLETED,
      undefined,
      SignInLogger.toDetails(fields)
    );
  }

  /**
   * Forgets the last screen shown, so the next `viewedScreen` reports no
   * previous screen. Call when the sign-in phase itself restarts.
   */
  public static resetScreenHistory(): void {
    SignInLogger.mPreviousScreenId = undefined;
  }

  // ───────────────────────────── Field helpers ─────────────────────────────

  /**
   * Error constructor name — never the message, which may embed the username
   * or other caller-supplied material echoed back by the service.
   */
  public static describeErrorType(error: unknown): string {
    if (error instanceof Error) {
      return error.constructor.name;
    }
    return UNKNOWN_ERROR_TYPE;
  }

  /**
   * HTTP status from an Axios-style error, as a string, or undefined when the
   * failure never reached a response (network, abort, timeout).
   */
  public static extractHttpStatus(error: unknown): string | undefined {
    if (error === null || error === undefined || typeof error !== "object") {
      return undefined;
    }
    const errorRecord = error as Record<string, unknown>;
    const response = errorRecord.response as
      | Record<string, unknown>
      | undefined;
    if (response === null || response === undefined) {
      return undefined;
    }
    const status: number = Number(response.status);
    if (Number.isNaN(status)) {
      return undefined;
    }
    return String(status);
  }

  /** Caller fields as a LogDetails, with undefined-valued keys dropped. */
  private static toDetails(fields?: SignInLogFields): LogDetails {
    const details: LogDetails = {};
    SignInLogger.assign(details, fields);
    return details;
  }

  /** Merges caller-supplied fields, skipping undefined so keys stay absent. */
  private static assign(target: LogDetails, extra?: SignInLogFields): void {
    if (extra === undefined || extra === null) {
      return;
    }
    const keys: string[] = Object.keys(extra);
    for (let index = 0; index < keys.length; index += 1) {
      const value: string | undefined = extra[keys[index]];
      if (value !== undefined) {
        target[keys[index]] = value;
      }
    }
  }
}
